import { z } from "zod";

import { SubjectType } from "../../../../shared/domain/permissions/permissions.types";
import { RoleCapabilitySchema } from "../../../../shared/domain/roles/role-capability.types";

/**
 * ORG-WIDE capability grants — `RoleCapabilityGrant`, the org half of the two
 * scopes `role-capability.types.ts`'s header describes.
 *
 * A row here says "this subject holds this capability across EVERY Role in the
 * organisation, including Roles that do not exist yet". It has no `roleId`
 * column, which is why `ORG_SCOPED_ONLY_CAPABILITIES` — `role.create` today —
 * can be held at all.
 *
 * 🚨 EVERY ROUTE BEHIND THESE SCHEMAS IS ORG-ADMIN-ONLY AND MUST STAY SO.
 * Whoever writes this table can grant themselves, or anyone else, every
 * capability in the catalog across every Role, permanently. See
 * `role-capability-grants.controller.ts` for the full argument, including why a
 * `@RequireRoleCapability` gate here would be a self-escalation loop.
 */

/**
 * The three subject kinds a Role capability grant can name.
 *
 * `SubjectType` has five members; `api_key` and `role` are legal on
 * `ResourcePermission` and meaningless for a capability, and the migration's
 * `RoleCapabilityGrant_subject_known` CHECK narrows the column to exactly these
 * three. Derived from the constant rather than spelled as string literals: the
 * stored values are LOWERCASE (`SubjectType.ORGANIZATION` is the TypeScript
 * KEY, `"organization"` is what it holds) and a hand-typed literal is how a
 * fixture ends up refused by that CHECK at 23514.
 */
export const ROLE_CAPABILITY_GRANT_SUBJECT_TYPES = [
  SubjectType.USER,
  SubjectType.GROUP,
  SubjectType.ORGANIZATION
] as const;

export const RoleCapabilityGrantSubjectTypeSchema = z.enum(ROLE_CAPABILITY_GRANT_SUBJECT_TYPES);

export type RoleCapabilityGrantSubjectType = z.infer<typeof RoleCapabilityGrantSubjectTypeSchema>;

/**
 * One stored grant, as the editor renders it.
 *
 * `capability` is the CATALOG enum and not a bare string, deliberately. The
 * column is TEXT with a shape CHECK only — the catalog is a const union in
 * `@nexus/types` chosen so it can grow without a migration — so the database
 * will hold a well-formed value this build has never heard of. Such a row
 * grants nothing (`isRoleCapability` drops it on every read path), and it is
 * dropped here too rather than widening this field to `string`: a value no
 * client can name is a value no client can act on.
 *
 * `subjectId` is non-nullable HERE even though the column is nullable. A row
 * storing `null` grants nothing — `roleCapabilityGrantOrgSubjectId` is the one
 * legal spelling of an org-wide subject and it is the organisation's own id —
 * so the read drops those too. Nothing in this codebase can write one.
 */
export const RoleCapabilityGrantSchema = z.object({
  id: z.string(),
  capability: RoleCapabilitySchema,
  subjectType: RoleCapabilityGrantSubjectTypeSchema,
  /**
   * The subject the grant names. For `organization` this is the organisation's
   * own id, echoed back — the server spells it, the caller never sends it.
   */
  subjectId: z.string(),
  createdAt: z.string()
});

export type RoleCapabilityGrant = z.infer<typeof RoleCapabilityGrantSchema>;

/**
 * The organisation's whole grant set, with its subjects.
 *
 * 🚨 `grants: null` IS THE TABLE BEING UNREADABLE. `grants: []` IS THE TABLE
 * READ AND EMPTY. THEY ARE DIFFERENT FACTS AND THE NULL IS WHY THIS FIELD IS
 * NULLABLE ON THE WIRE.
 *
 * `RoleCapabilityGrant`'s migration ships alongside the code that reads it, so
 * a pod can serve requests against a database with no such relation — measured
 * on production 2026-08-24, where the relation does not exist while the reader
 * is already deployed. `OrgCapabilityGrantsRead` on
 * `list-role-capabilities-for-org-subject.port.ts` carries the same union one
 * layer down and its header names this consumer by name: the grants EDITOR must
 * show "unavailable" rather than an empty list an administrator would "fix" by
 * re-granting rows that already exist.
 *
 * ⚠️ AND IT IS THE FIELD RATHER THAN A 503, DELIBERATELY. A 503 makes an
 * expected, bounded deploy-window state indistinguishable from a real outage,
 * and every HTTP client in the stack already retries-and-backs-off on it. The
 * distinction belongs in the body, where a client has to read it to render
 * anything at all.
 *
 * Only Postgres `undefined_table` produces the null — `is-missing-relation.ts`
 * is the predicate and every other failure still throws. A dropped connection,
 * a permission error or a statement timeout must not render as "unavailable,
 * probably fine".
 */
export const RoleCapabilityGrantsListResponseSchema = z.object({
  grants: z.array(RoleCapabilityGrantSchema).nullable()
});

/**
 * What a grant write says, and it is a DISCRIMINATED UNION so the illegal state
 * cannot be sent.
 *
 * 🔴 THE ORG-WIDE ARM CARRIES NO `subjectId`, AND THAT IS THE WHOLE REASON FOR
 * THE UNION. `roleCapabilityGrantOrgSubjectId` is the single legal spelling of
 * an org-wide subject — the organisation's own id, never null — and a caller
 * who sends a different one would either be silently corrected (a write that
 * does not do what it says) or refused with a message about a field they had no
 * business filling in. Removing the field removes the question: the server
 * spells the subject, and `subjectId` in the response is its word rather than
 * an echo of the caller's.
 *
 * `.trim()` before `.min(1)` on the two subject arms is not cosmetic. The
 * column's `RoleCapabilityGrant_subject_id_not_blank` CHECK refuses an id made
 * only of the six ASCII blanks, at 23514 — a database error rendering as a 500.
 * Refusing it here makes it a 400 that names the field, and JS `trim()` covers
 * every Unicode blank, which is a superset of what the CHECK strips.
 */
export const GrantOrgRoleCapabilityBodySchema = z.discriminatedUnion("subjectType", [
  z.object({
    subjectType: z.literal(SubjectType.USER),
    /** A `User.id` that must be a member of the CALLER's organisation. */
    subjectId: z.string().trim().min(1),
    capability: RoleCapabilitySchema
  }),
  z.object({
    subjectType: z.literal(SubjectType.GROUP),
    /** A `UserGroup.id` that must belong to the CALLER's organisation. */
    subjectId: z.string().trim().min(1),
    capability: RoleCapabilitySchema
  }),
  z.object({
    subjectType: z.literal(SubjectType.ORGANIZATION),
    capability: RoleCapabilitySchema
  })
]);

export type GrantOrgRoleCapabilityBody = z.infer<typeof GrantOrgRoleCapabilityBodySchema>;

/** The grant after the write — the same row a subsequent list would report. */
export const RoleCapabilityGrantResponseSchema = z.object({
  grant: RoleCapabilityGrantSchema
});

/**
 * Idempotent revoke: `false` means the grant was already gone, which is the
 * same end state and not an error. A grant belonging to another organisation is
 * indistinguishable from one that never existed.
 */
export const RevokeRoleCapabilityGrantResponseSchema = z.object({
  revoked: z.boolean()
});

/**
 * `grantId`, never `id` — a route parameter literally named `id` under `/roles`
 * is read as a Role id by `roleCapabilityTargetFromParams`, and by every guard
 * and gate keyed on it.
 */
export const RoleCapabilityGrantPathVarsSchema = z.object({
  grantId: z.string().uuid()
});
